import { Component } from '@theme/component';

const mobileMediaQuery = matchMedia('(max-width: 749px)');
const MOBILE_COLUMN_COUNT = 4;

/**
 * Expands one level-three product category and reveals its level-four links.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} [trigger]
 * @property {HTMLElement[]} [panel]
 * @property {HTMLElement} [details]
 * @property {HTMLElement} [detailsAnchor]
 *
 * @extends {Component<Refs>}
 */
class ProductCategoryMenu extends Component {
  /** @type {HTMLButtonElement | null} */
  #activeTrigger = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#closeAll();
    this.#activeTrigger = null;
    super.disconnectedCallback();
  }

  #bindEvents() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    mobileMediaQuery.addEventListener('change', this.#handleViewportChange, {
      signal: this.#abortController.signal,
    });
  }

  #handleViewportChange = () => {
    if (this.#activeTrigger) {
      this.#positionDetails(this.#activeTrigger);
      return;
    }

    this.#restoreDetails();
  };

  /** @param {MouseEvent} event */
  toggleCategory = (event) => {
    const trigger = this.#findTrigger(event.target);
    if (!trigger) return;

    const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';
    this.#closeAll();

    if (shouldOpen) this.#open(trigger);
  };

  /** @param {KeyboardEvent} event */
  handleDetailsKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#activeTrigger) return;

    event.preventDefault();
    const trigger = this.#activeTrigger;
    this.#closeAll();
    trigger.focus();
  };

  /** @param {KeyboardEvent} event */
  handleCategoryKeydown = (event) => {
    const trigger = this.#findTrigger(event.target);
    if (!trigger) return;

    if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
      event.preventDefault();
      this.#closeAll();
      trigger.focus();
      return;
    }

    const triggers = this.refs.trigger ?? [];
    const currentIndex = triggers.indexOf(trigger);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % triggers.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = triggers.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    triggers[nextIndex]?.focus();
  };

  #reset() {
    this.#activeTrigger = null;
    this.#closeAll();
  }

  #closeAll() {
    for (const trigger of this.refs.trigger ?? []) {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    }

    for (const panel of this.refs.panel ?? []) {
      panel.classList.remove('is-active');
      panel.hidden = true;
    }

    if (this.refs.details) this.refs.details.hidden = true;
    this.#restoreDetails();
    this.#activeTrigger = null;
  }

  /** @param {HTMLButtonElement} trigger */
  #open(trigger) {
    const targetId = trigger.dataset.categoryPanelTarget;
    if (!targetId) return;

    const panel = (this.refs.panel ?? []).find((candidate) => candidate.id === targetId);
    if (!panel) return;

    trigger.classList.add('is-active');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.classList.add('is-active');
    if (this.refs.details) {
      this.#positionDetails(trigger);
      this.refs.details.hidden = false;
    }
    this.#activeTrigger = trigger;
  }

  /** @param {HTMLButtonElement} trigger */
  #positionDetails(trigger) {
    const { details } = this.refs;
    if (!details) return;

    if (!mobileMediaQuery.matches) {
      this.#restoreDetails();
      return;
    }

    const item = trigger.closest('[data-category-menu-item], .product-category-menu__item');
    const rail = item?.parentElement;
    if (!(item instanceof HTMLElement) || !(rail instanceof HTMLElement)) return;
    if (!rail.matches('[data-category-menu-rail], .product-category-menu__rail')) return;

    const items = [...rail.children].filter((child) =>
      child.matches('[data-category-menu-item], .product-category-menu__item')
    );
    const itemIndex = items.indexOf(item);
    if (itemIndex < 0) return;

    // Insert the panel after the selected four-column row so visual and keyboard order stay aligned.
    const rowEndIndex =
      Math.min(Math.ceil((itemIndex + 1) / MOBILE_COLUMN_COUNT) * MOBILE_COLUMN_COUNT, items.length) - 1;
    const rowEndItem = items[rowEndIndex];
    if (!(rowEndItem instanceof HTMLElement)) return;

    details.setAttribute('role', 'listitem');
    rowEndItem.after(details);
  }

  #restoreDetails() {
    const { details, detailsAnchor } = this.refs;
    if (!details || !detailsAnchor) return;

    details.removeAttribute('role');
    if (detailsAnchor.nextElementSibling !== details) detailsAnchor.after(details);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLButtonElement | null}
   */
  #findTrigger(target) {
    if (!(target instanceof Element)) return null;

    const trigger = target.closest('[data-category-panel-target]');
    return trigger instanceof HTMLButtonElement && this.contains(trigger) ? trigger : null;
  }
}

if (!customElements.get('product-category-menu')) {
  customElements.define('product-category-menu', ProductCategoryMenu);
}
